// src/components/DiseaseSelector.tsx
// 疾患深掘りタブ の DPC6 selector。
// 旧 UI は <input type="text"> に 6 桁数字を直打ち、 だったが、
// 医師は DPC コード (6桁数字) を覚えていない → 「疾患名で検索」 が必須要件 (5/28 経営会議向け)。
//
// 実装: HTML5 <datalist> を使って native combobox 化。
//   - 候補は dim_dpc6 全件 (~515 件) + 直近 12 ヶ月の出現件数 n_cases
//   - 並びは「n_cases > 0」 を先頭、 次に n_cases DESC, dpc6 ASC (server 側で実施)
//   - 候補表示: 「010060 脳梗塞 (n=622)」 ← 入力が 「脳」 「010」 のどちらでも brower native filter で hit
//   - 確定値は f.dpc6 (string | null)。 入力先頭 6 桁数字を取り出して setDpc6
//   - クリア: × ボタンで setDpc6(null) (既存挙動を維持)
//
// 入力 bug fix (2026-05-26):
//   旧実装は value={displayValue} (= f.dpc6 から逆引きした文字列) を直接 controlled bind し、
//   onChange は「6 桁数字 match 時のみ」 setDpc6 を呼んでいた。
//   → ユーザが「脳」 と打った瞬間 React rerender で value="" に戻り、 入力した文字が即消える。
//   → local state `input` を持って 入力途中の文字も保持。
//   → 外部 (table 行クリック等) で f.dpc6 が変わったら useEffect で input を同期。
import { useEffect, useState } from "react";
import { useDpc6List } from "../lib/useDpc6List";
import { useFilters } from "../store";

export function DiseaseSelector() {
  const f = useFilters();
  const { data, isLoading, error } = useDpc6List();
  const rows = data?.rows ?? [];
  // 現在選択中の疾患名を逆引きして「010060 脳梗塞」 の形で表示
  const current = f.dpc6 ? rows.find((r) => r.dpc6 === f.dpc6) : undefined;

  // local state: 入力途中の文字 (「脳」 等) も保持できるようにする。
  // controlled input の value は常にこの local state を見る。
  const [input, setInput] = useState<string>("");

  // 外部 (table 行クリック / クエリパラメータ / クリア等) で f.dpc6 が変わったら input を同期。
  // current が読み込み終わってない (rows がまだ空) 場合は dpc6 のみで仮表示しておき、
  // rows 到着後に「010060 脳梗塞」 に置き換える。
  useEffect(() => {
    if (f.dpc6) {
      const label = current ? `${current.dpc6} ${current.疾患名}` : f.dpc6;
      setInput(label);
    } else {
      setInput("");
    }
  }, [f.dpc6, current]);

  return (
    <>
      <div className="flex items-center gap-1">
        <input
          list="dpc6-options"
          type="text"
          placeholder="例: 脳梗塞 / 010060"
          className="flex-1 min-w-0 border border-slate-300 rounded px-2 py-1 bg-white"
          value={input}
          disabled={isLoading}
          onChange={(e) => {
            const v = e.target.value;
            // 入力された文字は常に local state に反映 (= 文字が消えない)
            setInput(v);
            const trimmed = v.trim();
            if (trimmed === "") {
              if (f.dpc6 !== null) f.setDpc6(null);
              return;
            }
            // 先頭 6 桁数字を抽出 (datalist option value: "010060 脳梗塞 (n=622)"、
            // または ユーザが直接打った "010060")。 マッチしなければ未確定扱い (= setDpc6 を呼ばない)。
            // 「脳」 のような中途半端な入力では ranking は前回のまま、 input box にだけ文字が残る。
            const m = trimmed.match(/^(\d{6})/);
            if (m && m[1] !== f.dpc6) f.setDpc6(m[1]);
          }}
        />
        {f.dpc6 && (
          <button
            type="button"
            onClick={() => { f.setDpc6(null); setInput(""); }}
            className="text-[10px] px-1 text-red-600 hover:underline"
            title="クリア"
          >×</button>
        )}
      </div>
      <datalist id="dpc6-options">
        {rows.map((r) => {
          // n_cases が undefined (asOf 未指定) or 0 のときは括弧表記を省略
          const nLabel = (r.n_cases != null && r.n_cases > 0)
            ? ` (n=${r.n_cases})` : "";
          return (
            <option
              key={r.dpc6}
              value={`${r.dpc6} ${r.疾患名}${nLabel}`}
            />
          );
        })}
      </datalist>
      {isLoading && (
        <div className="text-[10px] text-slate-400 mt-1">疾患マスタ読込中…</div>
      )}
      {error && (
        <div className="text-[10px] text-red-600 mt-1">
          疾患マスタ取得失敗: {(error as Error).message}
        </div>
      )}
    </>
  );
}
